<template>
  <div class="project" :class="{ 'reverse-direction': reverse }">
    <div class="preview">
      <div class="img-wrapper" :class="[{border: this.platform != 'mobile' && this.decorate}, this.platform]">
        <terminal-border v-if="this.platform == 'web' && this.decorate" :title="this.name" />

        <img :src="require('@/assets/previews/' + this.preview)" class="img" />
      </div>
    </div>

    <div class="divider">
      <div
        class="date"
      >{{ (this.year != null && this.month != null) ? this.year + " - " + this.numToMonth(this.month) : "No Date" }}</div>
    </div>

    <div class="about text" :class="{ 'align-right': reverse }">
      <div class="inner">
        <div class="top" :class="{ 'justify-right': reverse }">
          <div class="name">{{ this.name }}</div>
          <div class="platform bg-green text strong">{{ this.platform.toUpperCase() }}</div>
        </div>
        <div class="description">{{ this.description }}</div>

        <div class="section my-role" :class="{ 'justify-right': reverse }" v-if="this.role != null">
          <div class="header">
            <span class="text bg-purple">My Role</span>
          </div>
          <div class="text content">{{ this.role }}</div>
        </div>

        <div
          class="section challenges"
          :class="{ 'justify-right': reverse }"
          v-if="this.challenges != null"
        >
          <div class="header">
            <span class="text bg-purple">Challenges</span>
          </div>
          <div class="text content">{{ this.challenges }}</div>
        </div>

        <div
          class="section features"
          :class="{ 'justify-right': reverse }"
          v-if="this.features != null"
        >
          <div class="header">
            <span class="text bg-purple">Notable Features</span>
          </div>
          <div class="item" v-for="(feature, index) in this.features" :key="index">
            <font-awesome-icon :icon="getIcon('feature')" class="icon" />
            {{ feature }}
          </div>
        </div>

        <div
          class="section technologies"
          :class="{ 'justify-right': reverse }"
          v-if="this.technologies != null"
        >
          <div class="header">
            <span class="text bg-blue">Technologies Used</span>
          </div>
          <div class="item" v-for="(technology, index) in this.technologies" :key="index">
            <font-awesome-icon :icon="getIcon(technology)" class="icon" />
            {{ technology }}
          </div>
        </div>

        <div
          class="section links"
          :class="{ 'justify-right': reverse }"
          v-if="this.links.length > 0"
        >
          VIEW ON
          <div
            class="item"
            v-for="(link, index) in this.links"
            :key="index"
            :class="{ 'disable': !link.hasOwnProperty('address')}"
            v-on:click="browseTo(link.address)"
            v-tooltip="link.msg"
          >
            <font-awesome-icon :icon="getIcon(link.platform)" class="icon" />
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
// Icons - Font awesome
import {
  faExternalLinkAlt,
  faRing,
  faDatabase,
  faListAlt,
  faTerminal,
  faGlobe
} from "@fortawesome/free-solid-svg-icons";
import {
  faGithub,
  faVuejs,
  faReact,
  faNodeJs,
  faPython,
  faHtml5,
  faPhp,
  faUnity,
  faAdobe,
  faDocker,
  faGitlab,
  faTrello,
  faStripe,
  faGooglePlay,
  faAppStoreIos,
  faWindows
} from "@fortawesome/free-brands-svg-icons";

// Custom
import TerminalBorder from "@/components/UI/TerminalBorder.vue";

export default {
  name: "project",
  components: {
    TerminalBorder
  },
  props: {
    name: {
      type: String,
      default: "Project Name",
      required: true
    },
    description: {
      type: String,
      default: "Project description here",
      required: true
    },
    year: {
      type: Number,
      default: null,
      required: false
    },
    month: {
      type: Number,
      default: null,
      required: false
    },
    role: {
      type: String,
      default: null,
      required: false
    },
    features: {
      type: Array,
      default: null,
      required: false
    },
    challenges: {
      type: String,
      default: null,
      required: false
    },
    technologies: {
      type: Array,
      default: null,
      required: false
    },
    preview: {
      type: String,
      default: "",
      required: true
    },
    reverse: {
      type: Boolean,
      default: false,
      required: false
    },
    platform: {
      type: String,
      default: "",
      required: false
    },
    decorate: {
      type: Boolean,
      default: true,
      required: false
    },
    links: {
      type: Array,
      default: () => [],
      required: false
    }
  },
  data() {
    return {};
  },
  methods: {
    getIcon(icon) {
      switch (icon.toLowerCase()) {
        case "github":
          return faGithub;
        case "web":
          return faExternalLinkAlt;
        case "nodejs":
          return faNodeJs;
        case "vuejs":
          return faVuejs;
        case "reactjs":
          return faReact;
        case "python":
          return faPython;
        case "html":
          return faHtml5;
        case "php":
          return faPhp;
        case "rabbitmq":
          return faListAlt;
        case "sql":
          return faDatabase;
        case "c#":
          return faTerminal;
        case "unity":
          return faUnity;
        case "photoshop":
          return faAdobe;
        case "docker":
          return faDocker;
        case "gitlab":
          return faGitlab;
        case "trello":
          return faTrello;
        case "stripe":
          return faStripe;
        case "website":
          return faGlobe;
        case "android-app":
          return faGooglePlay;
        case "ios-app":
          return faAppStoreIos;
        case "asp.net":
          return faWindows;
        default:
          return faRing;
      }
    },
    numToMonth(num) {
      switch (num) {
        case 1:
          return "January";
        case 2:
          return "February";
        case 3:
          return "March";
        case 4:
          return "April";
        case 5:
          return "May";
        case 6:
          return "June";
        case 7:
          return "July";
        case 8:
          return "August";
        case 9:
          return "September";
        case 10:
          return "October";
        case 11:
          return "November";
        case 12:
          return "December";
        default:
          return "January";
      }
    },
    // Go to link
    browseTo(address) {
      if (address != undefined && address != "" && address != null) {
        // console.log("Browsing to " + address);
        window.open(address);
      }
    }
  }
};
</script>

<style scoped>
.project {
  display: flex;
  flex-direction: row;
  padding: 0px 30px 0px 30px;
  min-height: 800px;
}
.reverse-direction {
  flex-direction: row-reverse !important;
}

@media only screen and (max-width: 1200px) {
  .project {
    flex-direction: column;
    margin-bottom: 70px;
  }

  .project .section {
    align-items: center !important;
    justify-content: center !important;
  }

  .project .header {
    align-items: center !important;
    justify-content: center !important;
    text-align: center;
  }

  .align-right {
    text-align: center !important;
  }

  .top {
    text-align: center !important;
    align-items: center !important;
    justify-content: center !important;
  }

  .reverse-direction {
    flex-direction: column !important;
  }

  .divider {
    display: none !important;
  }

  .preview {
    flex-basis: 500px !important;
  }
}

/* Preview */
.preview {
  flex: 1;
  flex-basis: 0;
  display: flex;
  justify-content: center;
  align-items: center;
  overflow: hidden;
}
.preview > .img-wrapper {
  width: 45%;
  /* overflow: hidden; */
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  transition: 0.2s;
}
.preview > .img-wrapper:hover {
  transform: scale(1.1, 1.1);
  /* box-shadow: 0px 2px 10px 4px rgba(0, 0, 0, 0.2); */
}
.preview img {
  width: 100%;
  height: 100%;
  object-fit: cover;
  overflow: hidden;
}
.preview .web {
  width: 65%;
}
.border {
  border: 7px solid #f1f1f1;
  border-top: none;
  border-radius: 6px;
}
.preview .mobile {
  width: 35%;
}

.top {
  display: flex;
  width: 100%;
  border-bottom: 3px solid #6c63ff;
  align-items: center;
  padding-bottom: 19px;
}

/* About */
.about {
  flex-grow: 1;
  flex-basis: 0;
  display: flex;
  justify-content: center;
  flex-direction: column;
  text-align: left;
  color: #e6e6e6;
}
.about > .inner {
  padding: 30px 10% 0px 17%;
}
.about .name {
  font-size: 1.8rem;
}
.about .description {
  font-size: 1rem;
  color: #cecece;
  padding: 20px 0px 8px 0px;
  line-height: 27px;
}
.about .platform {
  height: fit-content;
  padding: 7px;
  font-size: 14px;
  color: #e7e7e7;
  border-radius: 4px;
  margin-left: 14px;
  cursor: default;
}

.about .section {
  display: flex;
  flex-flow: wrap;
  padding: 25px 0px 15px 0px;
}
.about .section > .header {
  font-size: 17px;
  width: 100%;
  padding-bottom: 14px;
  cursor: default;
}
.about .section > .header > .text {
  padding: 6px 10px 6px 10px;
  border-radius: 5px;
}
.about .section > .content {
  padding: 0px 0px 0px 7px;
  color: #cecece;
  line-height: 27px;
}

.features .item {
  padding: 0px 8px 8px 8px;
  cursor: default;
}
.features .item > .icon {
  font-size: 0.9rem;
  color: white;
  padding-right: 2px;
  transition: 0.2s;
}
.features .item:hover > .icon {
  color: #6c63ff;
}

.technologies .item {
  padding: 0px 8px 8px 8px;
  cursor: default;
}
.technologies .item > .icon {
  font-size: 0.9rem;
  color: white;
  padding-right: 2px;
  transition: 0.2s;
}
.technologies .item:hover > .icon {
  color: #6c63ff;
}

/* Divider with date */
.divider {
  padding: 0px 0px 0px 0px;
  height: 700px;
  background-color: #232233;
  display: flex;
  align-items: center;
  width: 8px;
  flex-wrap: nowrap;
}
.divider > .date {
  font-size: 15px;
  color: white;
  transform: rotate(-90deg);
  white-space: nowrap;
  margin-left: -23px;
  background-color: #2b2a3f;
  padding: 10px 0px 10px 0px;
}

/* Links */
.links {
  font-size: 13px;
  display: flex;
  flex-direction: row;
  align-items: center;
  padding-top: 20px !important;
}
.links > .item:first-of-type {
  padding-left: 20px;
}
.links > .item {
  font-size: 23px;
  padding: 5px 9px 5px 9px;
  color: white;
  transition: 0.2s;
  cursor: pointer;
}
.links > .item:hover {
  color: #6c63ff;
}
.links > .item:hover:active {
  transform: scale(.9, .9);
}
.links > .disable {
  opacity: 0.2;
  cursor: default;
}

/* Tooltip */
.tooltip {
  display: block !important;
  z-index: 10000;
}

.tooltip .tooltip-inner {
  background: red;
  color: white;
  border-radius: 16px;
  padding: 5px 10px 4px;
}

.align-right {
  text-align: right;
}
.justify-right {
  justify-content: right;
}
</style>
